'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Pencil, Trash2, X, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { TableCell, TableRow } from '@/components/ui/table'
import { CountryMultiSelect } from '@/components/country-multi-select'
import { Badge } from '@/components/ui/badge'
import { countries } from '@/data/countries'
import type { TaxRateItem } from '@/lib/api/taxRates'

const allCountryCodes = countries.map((c) => c['alpha-2'])

export interface TaxRateFormData {
    name: string
    rate: number
    include: boolean
    applyToShipping: boolean
    countryCodes: string[]
    stateCodes: string[]
}

interface EditableTaxRateRowProps {
    taxRate?: TaxRateItem
    isEditing: boolean
    onSave: (data: TaxRateFormData) => Promise<void>
    onCancel: () => void
    onEdit: () => void
    onDelete: () => void
    isSaving: boolean
    selected: boolean
    onSelectChange: (checked: boolean) => void
}

function getInitialState(taxRate?: TaxRateItem): TaxRateFormData {
    return {
        name: taxRate?.name ?? '',
        rate: taxRate?.rate ?? 0,
        include: taxRate?.include ?? false,
        applyToShipping: taxRate?.applyToShipping ?? false,
        countryCodes: taxRate?.countryCodes ?? [],
        stateCodes: taxRate?.stateCodes ?? [],
    }
}

function formatCountries(codes: string[]): string {
    if (!codes.length) return '—'
    return codes
        .map((code) => {
            const country = countries.find((c) => c['country-code'] === code)
            return country ? country['alpha-2'] : code
        })
        .join(', ')
}

function formatStates(codes: string[]): string {
    if (!codes.length) return '—'
    return codes.join(', ')
}

export function EditableTaxRateRow({
    taxRate,
    isEditing,
    onSave,
    onCancel,
    onEdit,
    onDelete,
    isSaving,
    selected,
    onSelectChange,
}: EditableTaxRateRowProps) {
    const { t } = useTranslation('taxes')
    const [form, setForm] = React.useState<TaxRateFormData>(() => getInitialState(taxRate))
    const [stateCodesRaw, setStateCodesRaw] = React.useState(() => (taxRate?.stateCodes ?? []).join(', '))
    const [error, setError] = React.useState<string | null>(null)

    const handleSave = async () => {
        if (!form.name.trim()) {
            setError(t('form.information.name.required'))
            return
        }
        if (form.rate < 0 || form.rate > 100 || isNaN(form.rate)) {
            setError(t('form.information.rate.invalid'))
            return
        }
        setError(null)
        const stateCodes = stateCodesRaw
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        await onSave({ ...form, stateCodes })
    }

    const handleCancel = () => {
        setForm(getInitialState(taxRate))
        setStateCodesRaw((taxRate?.stateCodes ?? []).join(', '))
        setError(null)
        onCancel()
    }

    const updateField = <K extends keyof TaxRateFormData>(key: K, value: TaxRateFormData[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }))
    }

    if (!isEditing) {
        return (
            <TableRow>
                <TableCell className="w-10">
                    <Checkbox
                        checked={selected}
                        onCheckedChange={(checked) => onSelectChange(!!checked)}
                    />
                </TableCell>
                <TableCell className="font-medium">{taxRate?.name}</TableCell>
                <TableCell>{taxRate?.rate}%</TableCell>
                <TableCell>
                    {taxRate?.include && <Badge variant="secondary">{t('table.columns.included')}</Badge>}
                </TableCell>
                <TableCell>
                    {taxRate?.applyToShipping && <Badge variant="secondary">{t('table.columns.shipping')}</Badge>}
                </TableCell>
                <TableCell className="max-w-[150px] truncate text-sm text-muted-foreground">
                    {formatCountries(taxRate?.countryCodes ?? [])}
                </TableCell>
                <TableCell className="max-w-[120px] truncate text-sm text-muted-foreground">
                    {formatStates(taxRate?.stateCodes ?? [])}
                </TableCell>
                <TableCell>
                    <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
                            <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={onDelete}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </TableCell>
            </TableRow>
        )
    }

    return (
        <TableRow className="bg-muted/30">
            <TableCell className="w-10">
                <Checkbox
                    checked={selected}
                    onCheckedChange={(checked) => onSelectChange(!!checked)}
                    disabled
                />
            </TableCell>
            <TableCell>
                <Input
                    value={form.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder={t('form.information.name.placeholder')}
                    className="h-8 w-full min-w-[120px]"
                    autoFocus={!taxRate}
                />
            </TableCell>
            <TableCell>
                <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.rate}
                    onChange={(e) => updateField('rate', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    className="h-8 w-20"
                />
            </TableCell>
            <TableCell>
                <Switch
                    checked={form.include}
                    onCheckedChange={(checked) => updateField('include', checked)}
                />
            </TableCell>
            <TableCell>
                <Switch
                    checked={form.applyToShipping}
                    onCheckedChange={(checked) => updateField('applyToShipping', checked)}
                />
            </TableCell>
            <TableCell>
                <CountryMultiSelect
                    valueKey="country-code"
                    countryCodes={allCountryCodes}
                    selected={form.countryCodes}
                    onChange={(codes) => updateField('countryCodes', codes)}
                    className="min-w-[150px]"
                />
            </TableCell>
            <TableCell>
                <Input
                    value={stateCodesRaw}
                    onChange={(e) => setStateCodesRaw(e.target.value)}
                    placeholder="QC, ON, BC"
                    className="h-8 w-full min-w-[100px]"
                />
            </TableCell>
            <TableCell>
                <div className="flex items-center gap-1">
                    {error && (
                        <span className="text-xs text-destructive mr-1" title={error}>!</span>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-green-600"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    )
}
